﻿import { ValidatorFn, AbstractControl, FormControl, Validators } from '@angular/forms';
import { CurrencyPipe } from '@angular/common';
import { BorrowerService } from '../../shared/services/borrower/borrower.service';
import { FormValidationService } from '../../shared/fork-common-controls/form-validation.service';
import { Observable } from 'rxjs';
import { EmptyObservable } from 'rxjs/observable/EmptyObservable';
import { Utils } from '../../shared/fork-common-controls/utils.service';
import { UlaEmailControl } from '../../shared/ulaEmailControl';
import { PersonalLoanApplicationResponse } from '@shared/models';
import LoanType = PersonalLoanApplicationResponse.LoanTypeEnum;

export type UlaValidatorFlags = {
  required: boolean,
  valueType?: 'number' | 'currency' | null
};

type PredicateFn = () => boolean;

export class UlaValidators {
  private static emailTimeout;
  private static emailTimeoutPeriod = 500;
  private static _currencyPipe: CurrencyPipe = new CurrencyPipe('en-US');

  private static validateRepeatPassword(passwordControl: FormControl) {
    return (control: FormControl) => {

      let result = null;
      if (!!passwordControl.value && passwordControl.value.length > 0 && passwordControl.value !== control.value) {

        result = {
          passwordRepeat: {
            valid: false
          }
        };
      }

      return result;
    };
  }

  private static validateRepeatPasswordConditionally(passwordControl: FormControl) {
    return (control: FormControl) => {
      const result = Validators.required(control);
      return !!passwordControl.value ? result : null;
    };
  }

  private static validateLetterOrNumber(control: FormControl) {
    const letterOrNumberRegex = /[A-Za-z\d]/;

    let controlValue = control.value;

    return letterOrNumberRegex.test(controlValue) ? null : {
      letterOrNumber: {
        valid: false
      }
    };
  }

  private static validateSpecialCharacters(control: FormControl) {
    const specialCharRegex = /[!@#$%^&*()_\-+=,./<>?;:'"\[\]{}|~]/;

    let controlValue = control.value;

    return specialCharRegex.test(controlValue) ? null : {
        specialCharacter: {
          valid: false
        }
      };
  }

  private static get passwordValidators() {
    return Validators.compose(
      [Validators.required,
        Validators.minLength(8),
        Validators.pattern('[^`]+'),
        UlaValidators.validateLetterOrNumber,
        UlaValidators.validateSpecialCharacters]);
  }

  static getRepeatPasswordValidators(passwordControl: FormControl): ValidatorFn {
    return Validators.compose([UlaValidators.validateRepeatPassword(passwordControl),
      UlaValidators.validateRepeatPasswordConditionally(passwordControl)]);
  }

  static mustBeChecked(control: FormControl) {
    if (control.value !== true) {
      return {mustBeCheckedError: 'Must be checked'};
    }
    return null;
  }

  static mustAgree(control: FormControl) {
    if (control.value !== true) {
      return { mustAgreeError: 'Please agree to Terms of Use.' };
    }
    return null;
  }


  // this method is only suited for whole number inputs,
  // as it will remove the decimal point. If such a method is needed,
  // just extend this to take a boolean for whether it should
  // treat the value as a whole number.
  // for currency values see getCurrencyValueFromControl
  private static getIntValueFromControl(control: FormControl): number {
    let controlValue = control.value || 0;

    if (typeof controlValue === 'string') {
      controlValue = controlValue.replace(/[^\-\d]/g, '');
      controlValue = Number.parseInt(controlValue);
    }

    return controlValue;
  }

  static maxValue(maxValue: number): ValidatorFn {
    return (control: FormControl): {
      [key: string]: any;
    } => {
      let controlValue = UlaValidators.getIntValueFromControl(control);

      if (controlValue > maxValue) {
        return {maxValueError: `must be less than or equal to ${maxValue}`};
      }

      return null;
    };
  }

  static minValue(minValue: number): ValidatorFn {
    return (control: FormControl): {
      [key: string]: any;
    } => {
      const controlValue = UlaValidators.getIntValueFromControl(control);

      if (controlValue < minValue && (control.value || (control.value === 0 && 0 < minValue))) {
        return {maxValueError: `must be greater than or equal to ${minValue}`};
      }

      return null;
    };
  }

  // returns a floating point value represented by a currency,
  // assumed to be a string. Note that it removes all formatting
  // other than the decimal point.
  private static getCurrencyValueFromControl(control: FormControl): number {
    let controlValue = control.value;

    if (typeof controlValue === 'string') {
      controlValue = controlValue.replace(/[^\d.]/g, '');
      controlValue = Number.parseFloat(controlValue);
    }

    return controlValue;
  }

  static maxCurrencyValue(maxValue: number, useShortenedMessage: boolean = false): ValidatorFn {
    return (control: FormControl): {
      [key: string]: any;
    } => {
      let controlValue = UlaValidators.getCurrencyValueFromControl(control);

      if (controlValue > maxValue) {
        const maxValueCurrency = UlaValidators._currencyPipe.transform(maxValue, 'USD', 'symbol', '1.2-2');
        return useShortenedMessage
          ? { maxCurrencyShortError: `${maxValueCurrency} maximum amount`}
          : { maxCurrencyError: `must be ${maxValueCurrency} or less` };
      }

      return null;
    };
  }

  static minCurrencyValue(minValue: number, useShortenedMessage: boolean = false): ValidatorFn {
    return (control: FormControl): {
      [key: string]: any;
    } => {
      let controlValue = UlaValidators.getCurrencyValueFromControl(control);

      if (controlValue < minValue) {
        const minValueCurrency = UlaValidators._currencyPipe.transform(minValue, 'USD', 'symbol', '1.2-2');
        return useShortenedMessage
          ? { minCurrencyShortError: `${minValueCurrency} minimum amount` }
          : { minCurrencyError: `must be ${minValueCurrency} or more` };
      }

      return null;
    };
  }

  static noEmptyText(): ValidatorFn {
    return (control: FormControl): {
      [key: string]: any
    } => {
      let errorResult = null;
      let value = (<string>control.value);
      let controlValue = value ? value.replace(/\s/g, '') : value;

      if (!controlValue) {
        errorResult = {required: ''};
      }

      return errorResult;
    };
  }

  static getAllPasswordValidators() {
    return UlaValidators.passwordValidators;
  }

  static validateUniqueEmail(borrowerService: BorrowerService, email: string, errorCallback: () => void) {

    // Using set timeout here to modulate the frequency of calls to the email availability service.
    // To use debounce, we would have to get to the input source and directly subscribe to it with a debounce.
    // Since we want to do email duplication as a validation function, this function is called with every key stroke.
    clearTimeout(UlaValidators.emailTimeout);

    return new Observable(observer => {
      UlaValidators.emailTimeout = setTimeout(() => {
        if (!email) {
          observer.next(null);
        } else {
          borrowerService.borrowerEmailAvailability(email).subscribe(data => {
            if (!data.isAvailable) {
              const signInLink = '<a href="https://myloandepot.com">please sign in</a>';
              const message = `This email is already in use if you would like to continue a loan application
              in process, ${signInLink}. If you would like to apply for a new loan, please contact
              your loan officer or dial the phone number listed below.`;
              observer.next({emailInUse: message});
            } else {
              observer.next(null);
            }
          }, err => {
            console.error(err);
            if (errorCallback) {
              errorCallback();
            }
          });
        }
      }, UlaValidators.emailTimeoutPeriod);
    });
  }

  static getAsyncEmailValidators(borrowerService: BorrowerService,
                             // the predicate function has a default value,
                             // since only 1 instance of this validator has conditional logic,
                             // in the other 2 instances, the check should always fire
                             predicate: (c: UlaEmailControl) => boolean = (c) => true): ValidatorFn {
    return (c: UlaEmailControl): Observable<{[key: string]: any}> => {
      if (predicate(c)) {
        const email = !!c.value ? c.value : '';

        return UlaValidators.validateUniqueEmail(borrowerService, email, c.errorCallback).distinctUntilChanged().first();
      } else {
        return new EmptyObservable();
      }
    };
  }

  static getAllFirstNameValidators_PL(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      Validators.maxLength(formValidationService.maxlength.firstName.pl)
    ]);
  }

  static getAllFirstNameValidators_MortgageFlows(formValidationService: FormValidationService, required: boolean = true) {
    let validatorArray = [Validators.maxLength(formValidationService.maxlength.firstName.mortgage)];

    if (required) {
      validatorArray.push(Validators.required);
    }
    return Validators.compose(validatorArray);
  }

  static getAllLastNameValidators_MortgageFlows(formValidationService: FormValidationService, required: boolean = false) {
    let validatorArray = [Validators.maxLength(formValidationService.maxlength.lastName.mortgage)];

    if (required) {
      validatorArray.push(Validators.required);
    }
    return Validators.compose(validatorArray);
  }

  static getAllMiddleNameValidators_MortgageFlows(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.maxLength(formValidationService.maxlength.middleName)
    ]);
  }

  static getAllFirstAndLastNameValidators_PLFlows(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      Validators.maxLength(formValidationService.maxlength.firstAndLastName.pl)
    ]);
  }

  static getAllEmailValidators(formValidationService: FormValidationService, required: boolean = true) {
    let validatorArray = [Validators.pattern(formValidationService.regex.email),
      Validators.maxLength(formValidationService.maxlength.email)];

    if (required) {
      validatorArray.push(Validators.required);
    }
    return Validators.compose(validatorArray);
  }

  static getAllFirstAndLastNameValidators_MortgageFlows(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      Validators.maxLength(formValidationService.maxlength.firstAndLastName.mortgage)
    ]);
  }

  // TODO: remove this once we determine how validation will be handled on a single address field
  // or if it will be broken out into separate fields.
  static getAllSingleAddressFieldValidators_MortgageFlows(formValidationService: FormValidationService) {
    let maxLength = formValidationService.maxlength;
    let length = maxLength.address.mortgage;
    length += maxLength.city.mortgage;
    length += maxLength.state;
    length += maxLength.zipCode;
    return Validators.compose([
      Validators.required,
      UlaValidators.validateAddressOtherThanPOBox,
      Validators.maxLength(length)
    ]);
  }

  static getAllAddressValidators_MortgageFlows(formValidationService: FormValidationService, required: boolean = true) {
    let validatorArray = [
      UlaValidators.validateAddressOtherThanPOBox,
      UlaValidators.validateAddressNumberMaxLimit,
      Validators.maxLength(formValidationService.maxlength.address.mortgage)
    ];

    if (required) {
      validatorArray.push(Validators.required);
    }

    return Validators.compose(validatorArray);
  }

  static getAllMailingAddressValidators_MortgageFlows(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      Validators.maxLength(formValidationService.maxlength.address.mortgage)
    ]);
  }

  static getAllWorkAddressValidators_MortgageFlows(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      UlaValidators.noEmptyText(),
      UlaValidators.validateAddressNumberMaxLimit,
      Validators.maxLength(formValidationService.maxlength.workAddress.mortgage)
    ]);
  }

  static getAllAddressUnitValidators_MortgageFlows(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.maxLength(formValidationService.maxlength.addressUnit.mortgage)
    ]);
  }

  static getAllCityValidators_MortgageFlows(formValidationService: FormValidationService, required: boolean = true) {
    let validatorArray = [Validators.maxLength(formValidationService.maxlength.city.mortgage)];

    if (required) {
      validatorArray.push(Validators.required);
      validatorArray.push(UlaValidators.noEmptyText());
    }

    return Validators.compose(validatorArray);
  }

  static getAllCityValidators_PLFlows(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      Validators.maxLength(formValidationService.maxlength.city.pl)
    ]);
  }

  static getEmployerNameValidators_MortgageFlows(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      UlaValidators.noEmptyText(),
      Validators.maxLength(formValidationService.maxlength.employerName.mortgage)
    ]);
  }

  static getEmployeePositionValidators_MortgageFlows(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      UlaValidators.noEmptyText(),
      Validators.maxLength(formValidationService.maxlength.employeePosition.mortgage)
    ]);
  }

  static getYearsWithEmployerValidators_MortgageFlows(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      UlaValidators.maxValue(formValidationService.max.yearsWithEmployer.mortgage)
    ]);
  }

  static validateCurrency(control: FormControl) {
    const currencyRegex = /^[+-]?[0-9]{1,3}(?:,?[0-9]{3})*(?:\.[0-9]{2})?$/;

    return !control.value || currencyRegex.test(control.value) ? null : {
      currency: {
        valid: false
      }
    };
  }

  static validateAddressNumberMaxLimit(control: FormControl): any {
    const maxNumberLimitRegex = /^\d{9,}/;
    let result = null;

    if (maxNumberLimitRegex.test(control.value)) {
      result = {
        addressNumberMaxLimit: 'Address number exceeds 8 digits.'
      };
    }

    return result;
  }

  static getStateLicensingValidator({ state }): ValidatorFn {
    if (state === null) {

      // State Lending is not enabled.
      // Support old static config.
      state = {
        'WV': false
      };
    }

    const validateStateLicensing = (control: FormControl) => {
      let result = null;
      const stateAbbrev = control.value;

      if (state.hasOwnProperty(stateAbbrev) && state[stateAbbrev] !== true) {
        result = {
          stateLicensing: `Loans unavailable in ${ stateAbbrev }.`
        };
      }

      return result;
    };

    return validateStateLicensing;
  }

  public static onlyNumeric(control: FormControl) {
    const value = (control && control.value) || '';
    const bad = value ? /[^0-9]/.test(value.toString()) : true;
    return bad ? { nonNumericInput: 'Input must be numeric' } : null;
  }

  static positiveCurrencyValidator(formValidationService: FormValidationService) {
    return Validators.pattern(formValidationService.regex.positiveCurrency);
  }

  static validateAddressOtherThanPOBox(control: FormControl) {
    const poBoxRegex = /(p\.o\.)|(p\.?o\.? box)|(post office)|(post office box)/i; // trailing 'i' indicates case-insensitive search.

    return !!control.value && poBoxRegex.test(control.value) ?
      {
        // TODO find a better way to show display name on error messages.
        // Using '@ddress' so the string replace that sets display names won't set it to 'Address'.
        poBoxNotAllowed: 'PO Box is not allowed. Enter the street @ddress where you live.'
      } :
      null;
  }

  static spouseOnLoanRequired(maritalStatusControl: FormControl, otherBorrowersOnLoanControl: FormControl) {
    return (control: FormControl) => {
      let errorResult = null;
      if ((maritalStatusControl.value === 'Married' || maritalStatusControl.value === 'Separated')
            && otherBorrowersOnLoanControl.value && control.value === null) {
        errorResult = {required: ''};
      }
      return errorResult;
    };
  }

  static getYearsResidentValidators(fvService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      UlaValidators.minValue(0),
      UlaValidators.maxValue(fvService.max.yearsResidency)
    ]);
  }

  static getMonthsResidentValidators(fvService: FormValidationService, fieldIsRequired = true) {
    let validatorGroup: ValidatorFn[] = [
      UlaValidators.minValue(0),
      UlaValidators.maxValue(fvService.max.monthsResidency)
    ];

    if (fieldIsRequired) {
      validatorGroup.unshift(Validators.required);
    }

    return Validators.compose(validatorGroup);
  }

  static getNumDependentsValidators(fvService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      UlaValidators.minValue(0),
      UlaValidators.maxValue(fvService.max.numDependents.mortgage)
    ]);
  }

  static getDependentsAgeValidators(fvService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      UlaValidators.minValue(1),
      UlaValidators.maxValue(fvService.max.dependentsAge.mortgage)
    ]);
  }

  static getNumBorrowersOnLoanRequired(otherBorrowersControl: FormControl) {
    return (control: FormControl) => {
      let errorResult = null;
      if (otherBorrowersControl.value && !control.value) {
        errorResult = {required: ''};
      }
      return errorResult;
    };
  }

  static requestedGovernmentInfoRequired(noInformationControl: FormControl) {
    return (control: FormControl) => {
      let errorResult = null;
      if (!noInformationControl.value && !control.value) {
        errorResult = {required: ''};
      }
      return errorResult;
    };
  }

  static checkboxGovernmentInfoRequired(genderInfoControl: FormControl,
                                        ethnicityInfoControl: FormControl,
                                        raceOrOriginInfoControl: FormControl) {
    return (control: FormControl) => {
      let errorResult = null;
      if (!genderInfoControl.value && !ethnicityInfoControl.value && !raceOrOriginInfoControl.value && !control.value) {
        errorResult = {required: ''};
      }
      return errorResult;
    };
  }

  static getAllZipCodeValidators(formValidationService: FormValidationService, required: boolean = true) {
    let validatorArray = [Validators.pattern(formValidationService.regex.zipCode),
      Validators.maxLength(formValidationService.maxlength.zipCode)
    ];

    if (required) {
      validatorArray.push(Validators.required);
    }

    return Validators.compose(validatorArray);
  }

  static getAllPhoneValidators(formValidationService: FormValidationService, required: boolean = true) {
    let validatorArray = [Validators.maxLength(formValidationService.maxlength.phone),
      Validators.pattern(formValidationService.regex.phone)];

    if (required) {
      validatorArray.push(Validators.required);
    }
    return Validators.compose(validatorArray);
  }

  static getAllPhoneTypeValidators() {
    return Validators.compose([
      Validators.required
    ]);
  }

  static getAllPhoneExtensionValidators(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.pattern(formValidationService.regex.phoneExt)
    ]);
  }

  static getAllDateValidators_MortgageFlows(formValidationService: FormValidationService,
                                            mustBeAdult?: boolean,
                                            allowFutureDates: boolean = false,
                                            required: boolean = true) {
    let validatorArray = [
      Validators.maxLength(formValidationService.maxlength.date),
      UlaValidators.mustBeValidDate,
      Validators.pattern(formValidationService.regex.date)
    ];

    if (required) {
      validatorArray.push(Validators.required);
    }

    if (!allowFutureDates) {
      validatorArray.push(UlaValidators.validateDateMustBeInThePast);
    }

    if (!!mustBeAdult) {

      validatorArray.push(UlaValidators.getMinAgeStateValidator());
    }

    return Validators.compose(validatorArray);
  }

  // stateControl is an optional parameter.
  // only pass it in if the control needs to validate a date against a user's minimum age
  static getAllDateValidators(formValidationService: FormValidationService,
                              stateControl?: FormControl,
                              allowFutureDates: boolean = false,
                              isRequired: boolean = true) {
    let validatorArray = [
      Validators.maxLength(formValidationService.maxlength.date),
      Validators.pattern(formValidationService.regex.date),
      UlaValidators.mustBeValidDate
    ];

    if (!allowFutureDates) {
      validatorArray.push(UlaValidators.validateDateMustBeInThePast);
    }

    if (!!stateControl) {
      validatorArray.push(UlaValidators.getMinAgeStateValidator(stateControl));
    }

    if (isRequired) {
      validatorArray.push(Validators.required);
    }

    return Validators.compose(validatorArray);
  }

  static getLoanAmountValidator(prequalifiedBounds: {minimum: number, maximum: number}) {
    return (control: FormControl) => {
      let loanAmount = control.value;

      if (typeof loanAmount === 'string') {
        loanAmount = loanAmount.replace(/[^\d.]/g, '');
        loanAmount = Number.parseFloat(loanAmount);
      }

      return (control.value && prequalifiedBounds.minimum <= loanAmount && loanAmount <= prequalifiedBounds.maximum)
        ? null
        : {
          loanAmountInvalid: 'Enter amount within range.'
        };
    };
  }

  static getAllSSNValidators(formValidationService: FormValidationService,
                             otherSsnControl: FormControl = null) {

    let validatorArray = [
      Validators.pattern(formValidationService.regex.ssn),
      Validators.required
    ];

    if (!!otherSsnControl) {
        validatorArray.push((control: FormControl): { [key: string]: any } => {
            let ret = null;

            if (control.value === otherSsnControl.value) {
                ret = { duplicateSsn: 'Primary and Co-Borrower SSN cannot be the same' };
            }

            return ret;
        });
    }

    return Validators.compose(validatorArray);
  }

  static getStartDateValidator(dateToValidateAgainst) {
    return (control: FormControl): {
      [key: string]: any;
    } => {
      const diffBetweenDates = Utils.calculateDifferenceBetweenDates(dateToValidateAgainst.value, control.value);
      if (diffBetweenDates >= 0 && dateToValidateAgainst.value) {
        return {genericError: 'Start date must be before end date.'};
      }

      return null;
    };
  }

  static getYearAcquiredValidator(dateToValidateAgainst) {
    return (control: FormControl): {
      [key: string]: any;
    } => {
      const diffBetweenDates = dateToValidateAgainst.value - control.value;
      if (diffBetweenDates > 0 && dateToValidateAgainst.value) {
        return {genericError: 'Year Acquired cannot be before Year Built.'};
      }

      return null;
    };
  }

  static getCoBorrowerEmailValidator(email) {
    return (control: FormControl): {
      [key: string]: any;
    } => {
      if (control.value === email) {
        return {genericError: 'Email address must be unique.'};
      }

      return null;
    };
  }

  static getEndDateValidator(dateToValidateAgainst) {
    return (control: FormControl): {
      [key: string]: any;
    } => {
      const diffBetweenDates = Utils.calculateDifferenceBetweenDates(dateToValidateAgainst.value, control.value);
      if (diffBetweenDates <= 0 && dateToValidateAgainst.value) {
        return {genericError: 'End date must be after start date.'};
      }

      return null;
    };
  }

  static getConditionalEndDateValidator(dateToValidateAgainst, predicateFn: PredicateFn) {
    return (control: FormControl): {
      [key: string]: any;
    } => {

      const diffBetweenDates = Utils.calculateDifferenceBetweenDates(dateToValidateAgainst.value, control.value);
      if (predicateFn() && diffBetweenDates <= 0 && dateToValidateAgainst.value) {
        return {genericError: 'End date must be after start date.'};
      };

      return null;
    };
  }

  static getWorkStatusEndDateValidators(formValidationService: FormValidationService,
                                        startDateControl: FormControl,
                                        predicateFn: PredicateFn) {
    let validatorArr = [
      UlaValidators.getConditionalRequiredValidators(predicateFn),
      UlaValidators.getConditionalEndDateValidator(startDateControl, predicateFn),
      UlaValidators.conditionallyValidateDateMustBeInThePast(predicateFn),
      UlaValidators.getConditionalPatternValidator(predicateFn, formValidationService.regex.date),
      UlaValidators.getConditionalMaxLengthValidator(predicateFn, formValidationService.maxlength.date)
    ];

    return Validators.compose(validatorArr);
  }

  static getWorkStatusStartDateValidators(formValidationService: FormValidationService,
                                         militaryPayOtherDateControl: FormControl) {
    return Validators.compose([
      UlaValidators.getAllDateValidators(formValidationService),
      UlaValidators.getStartDateValidator(militaryPayOtherDateControl)
    ]);
  }

  static getDelinquencyExplanationValidation(defaultDelinquencyControl: FormControl,
                                             formValidationService: FormValidationService) {
    return (control: FormControl) => {
      let errorResult = null;
      if (defaultDelinquencyControl.value && !control.value) {
        errorResult = {required: 'Enter a value.'};
      }
      if (control.value && control.value.length > formValidationService.maxlength.explanation) {
        errorResult = {maxlength: {requiredLength: '255'}};
      }
      return errorResult;
    };
  }

  static getOwnershipInterestValidation(occupyPropertyControl: FormControl, ownershipInterestControl: FormControl) {
    return (control: FormControl) => {
      let errorResult = null;
      if (occupyPropertyControl.value && ownershipInterestControl.value && !control.value) {
        errorResult = {required: 'Enter a value.'};
      }
      return errorResult;
    };
  }

  static validateDateMustBeInThePast(dateControl: FormControl) {
    if (!dateControl.value) {
      return null;
    }

    const outcome = Utils.calculateMillisecondDiffFromToday(dateControl.value);
    return outcome > 0 ? {genericError: 'Cannot select a future date.'} : null;
  }

  static validateEmpDateConditionally(yearsWithEmployerControl: FormControl) {
    return (control: FormControl) => {
      const result = Validators.required(control);
      return (yearsWithEmployerControl.value === '-1') ? result : null;
    };
  }

  static validateEmpDateMustBeInThePast(yearsWithEmployerControl: FormControl) {
    return (control: FormControl) => {
      let result = null;
      if (yearsWithEmployerControl.value === '-1') {
        result = UlaValidators.validateDateMustBeInThePast(control);
      }
      return result;
    };
  }

  static validateDateMustBeWithinOneYear(yearsWithEmployerControl: FormControl) {
    return (control: FormControl) => {
      if (yearsWithEmployerControl.value === '-1') {
        const outcome = Utils.calculateDayDiffFromToday(control.value);
        return outcome < -365 ? {genericError: 'Select a date within a year.'} : null;
      } else {
        return null;
      }
    };
  }

  static dateCantBeBeforeToday(dateControl: FormControl) {
    if (!dateControl.value) {
      return null;
    }

    const outcome = Utils.calculateMillisecondDiffFromToday(dateControl.value);
    return outcome < 0 ? {genericError: 'Cannot select a past date.'} : null;
  }

  static getMinAgeStateValidator(stateControl?: FormControl) {
    return (dobControl: FormControl) => {
      if (!dobControl.value) {
        return null;
      }

      // Creating a date from a string creates it at midnight UTC time.
      // Whereas creating it from now, creates it at this time plus timezone offset.
      // All dates display in local timezone.
      let birthdate = new Date(dobControl.value);
      const diff = birthdate.getTimezoneOffset() * 60 * 1000;
      birthdate = new Date(birthdate.getTime() + diff);
      const now = new Date();

      const birthyear = birthdate.getFullYear();
      const birthmonth = birthdate.getMonth();
      const birthday = birthdate.getDate();

      const thisyear = now.getFullYear();
      const thismonth = now.getMonth();
      const thisday = now.getDate();

      let age = thisyear - birthyear;
      if (thismonth < birthmonth) {
        age--;
      } else if (thismonth === birthmonth) {
        if (thisday < birthday) {
          age--;
        }
      }

      // If nothing prevents the user from inputting his birthdate in the future, the age could appear as less than zero.
      if (age < 19 && age > 0
        && !!stateControl && !!stateControl.value
        && (stateControl.value === 'AL' || stateControl.value === 'NE')) {
        return {
          minAge: 'You must be 19 or older to apply for a loan.'
        };
      } else if (age < 18 && age >= 0) {
        return {
          minAge: 'You must be 18 or older to apply for a loan.'
        };
      } else {
        return null;
      }
    };
  }

  static getHomePaymentValidator(occupancyStatus: FormControl, leadGuid: string) {
    return (control: FormControl) => {
      let errorResult = null;
      if ((occupancyStatus.value === 'Rent' || occupancyStatus.value === 'Mortgage') && !control.value) {
        errorResult = {
          homePaymentRequired: 'Home payment is required for occupancy types Rent and Mortgage'
        };
      }

      if (leadGuid && !control.value) {
        errorResult = {
          homePaymentRequired: 'Home payment is required'
        };
      }
      return errorResult;
    };
  }

  static getAllYearlyIncomeValidators_PLFlows(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      Validators.pattern(formValidationService.regex.positiveCurrency),
      UlaValidators.maxCurrencyValue(formValidationService.max.yearlyIncome.pl)
    ]);
  }

  static getAllYearlyIncomeValidators_MortgageFlows(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      Validators.pattern(formValidationService.regex.positiveCurrency),
      UlaValidators.maxCurrencyValue(formValidationService.max.employedAnnualSalary.mortgage)
    ]);
  }

  static getIncomeLengthValidators_MortgageFlows(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.pattern(formValidationService.regex.positiveCurrency),
      UlaValidators.maxCurrencyValue(formValidationService.max.employedAnnualSalary.mortgage)
    ]);
  }

  static getAllAddressValidators() {
    return Validators.compose([
      Validators.required,
      UlaValidators.validateAddressOtherThanPOBox,
      UlaValidators.validateAddressNumberMaxLimit
    ]);
  }

  static getEmpDetailsNameValidator(employmentStatus: FormControl, formValidationService: FormValidationService) {
    return (control: FormControl) => {
      let errorResult = null;
      let regexp = new RegExp(formValidationService.regex.employerName);
      let errorEnforced = ('Employed' === employmentStatus.value) ||
        ('Self Employed' === employmentStatus.value) ||
        ('Military' === employmentStatus.value);
      if (errorEnforced && !control.value) {
        errorResult = {required: ''};
      } else if (errorEnforced && control.value) {
        if (control.value.length > 255) {
          errorResult = {maxlength: {requiredLength: '255'}};
        } else if (!regexp.test(control.value)) {
          errorResult = {required: ''};
        }
      }
      return errorResult;
    };
  }

  static getEmpDetailsPositionValidator(employmentStatus: FormControl, formValidationService: FormValidationService) {
    return (control: FormControl) => {
      let errorResult = null;
      let regexp = new RegExp(formValidationService.regex.employmentPosition);
      let errorEnforced = ('Employed' === employmentStatus.value) ||
        ('Self Employed' === employmentStatus.value) ||
        ('Military' === employmentStatus.value);
      if (errorEnforced && !control.value) {
        errorResult = {required: ''};
      } else if (errorEnforced && control.value) {
        if (control.value.length > 255) {
          errorResult = {maxlength: {requiredLength: '255'}};
        } else if (!regexp.test(control.value)) {
          errorResult = {required: ''};
        }
      }
      return errorResult;
    };
  }

  static getEmpDetailsYearsValidator(employmentStatus: FormControl) {
    return (control: FormControl) => {
      let errorResult = null;
      let errorEnforced = ('Employed' === employmentStatus.value) ||
        ('Self Employed' === employmentStatus.value) ||
        ('Military' === employmentStatus.value);
      if (errorEnforced && !control.value) {
        errorResult = {required: ''};
      }
      return errorResult;
    };
  }

  static getEmpDetailsStartDateValidator(yearsWithEmployerControl: FormControl) {
    let validatorArray = [
      UlaValidators.validateEmpDateConditionally(yearsWithEmployerControl),
      UlaValidators.validateEmpDateMustBeInThePast(yearsWithEmployerControl),
      UlaValidators.validateDateMustBeWithinOneYear(yearsWithEmployerControl)
    ];
    return Validators.compose(validatorArray);
  }

  static getEmpDetailsWorkAddressValidator(employmentStatus: FormControl) {
    let validatorArray = [
      UlaValidators.validateAddressOtherThanPOBox,
      UlaValidators.validateAddressNumberMaxLimit
    ];

    let statusIsEmployed = ('Employed' === employmentStatus.value);

    statusIsEmployed = (statusIsEmployed || ('Self Employed' === employmentStatus.value));
    statusIsEmployed = (statusIsEmployed || ('Military' === employmentStatus.value));

    if (statusIsEmployed) {
      validatorArray.push(Validators.required);
    }

    return Validators.compose(validatorArray);
  };

  static getEmpDetailsWorkCityValidator(employmentStatus: FormControl, formValidationService: FormValidationService) {
    return (control: FormControl) => {
      let errorResult = null;
      let regexp = new RegExp(formValidationService.regex.workCity);
      let errorEnforced = ('Employed' === employmentStatus.value)
        || ('Self Employed' === employmentStatus.value)
        || ('Military' === employmentStatus.value);

      if (errorEnforced && !control.value) {
        errorResult = {required: ''};
      } else if (errorEnforced && control.value && !regexp.test(control.value)) {
        errorResult = {maxlength: {requiredLength: '255'}};
      }
      return errorResult;
    };
  }

  static getEmpDetailsWorkStateValidator(employmentStatus: FormControl) {
    return (control: FormControl) => {
      let errorResult = null;
      let errorEnforced = ('Employed' === employmentStatus.value)
        || ('Self Employed' === employmentStatus.value)
        || ('Military' === employmentStatus.value);
      if (errorEnforced && !control.value) {
        errorResult = {required: ''};
      }
      return errorResult;
    };
  }

  static getEmpDetailsWorkZipValidator(employmentStatus: FormControl, formValidationService: FormValidationService) {
    return (control: FormControl) => {
      let errorResult = null;
      let regexp = new RegExp(formValidationService.regex.zipCode);
      let errorEnforced = ('Employed' === employmentStatus.value)
        || ('Self Employed' === employmentStatus.value)
        || ('Military' === employmentStatus.value);
      if (errorEnforced && !control.value) {
        errorResult = {required: ''};
      } else if (errorEnforced && control.value && !regexp.test(control.value)) {
        errorResult = {pattern: ''};
      }
      return errorResult;
    };
  }

  static getEmpDetailsWorkPhoneValidator(employmentStatus: FormControl, formValidationService: FormValidationService) {
    return (control: FormControl) => {
      let errorResult = null;
      let regexp = new RegExp(formValidationService.regex.workPhone);
      let errorEnforced = ('Employed' === employmentStatus.value)
        || ('Self Employed' === employmentStatus.value)
        || ('Military' === employmentStatus.value);
      if (errorEnforced && !control.value) {
        errorResult = {required: ''};
      } else if (errorEnforced && control.value && !regexp.test(control.value)) {
        if (control.value.length === 14) {
          errorResult = {pattern: ''};
        } else {
          errorResult = {minlength: {requiredLength: '10'}};
        }
      }
      return errorResult;
    };
  }

  static getAllDriversLicenseNumberValidators_MortgageFlows(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      Validators.maxLength(13),
      Validators.pattern(formValidationService.regex.driversLicenseNumber.mortgage)
    ]);
  }

  static getWelcomePageLoanAmountValidators_PersonalLoansFlows(
    formValidationService: FormValidationService,
    useShortenedErrorMessage: boolean = false,
    loanType: LoanType = LoanType.Personal) {

    // @TODO Get min/max values from CLP AppConfiguration
    let maxValue = 35000;
    if (loanType === LoanType.HomeImprovement) {
      maxValue = 75000;
    }
    return Validators.compose([
      Validators.required,
      Validators.pattern(formValidationService.regex.positiveCurrency),
      UlaValidators.minCurrencyValue(5000, useShortenedErrorMessage),
      UlaValidators.maxCurrencyValue(maxValue, useShortenedErrorMessage)
    ]);
  }

  static getWelcomePagePromoCodeValidators_PersonalLoansFlows(
    formValidationService: FormValidationService,
    isBetaPeriod: boolean) {
    if (isBetaPeriod) {
     return Validators.compose([
       Validators.required,
       Validators.pattern(formValidationService.regex.promoCode)
     ]);
    } else {
     return null;
    }
  }

  static getTrialDepositValidators(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      Validators.pattern(formValidationService.regex.positiveCurrency),
      UlaValidators.maxCurrencyValue(0.99)
    ]);
  }

  static getMonthlyMortgagePaymentValidators_MortgageFlows(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      Validators.pattern(formValidationService.regex.positiveCurrency),
      UlaValidators.maxCurrencyValue(formValidationService.max.monthlyMortgagePayment)
    ]);
  }

  static getRemainingMortgageBalanceValidators_MortgageFlows(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      Validators.pattern(formValidationService.regex.positiveCurrency),
      UlaValidators.maxCurrencyValue(formValidationService.max.remainingMortgageBalance)
    ]);
  }

  static getMonthlyRentalPaymentValidators_MortgageFlows(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      Validators.pattern(formValidationService.regex.positiveCurrency),
      UlaValidators.maxCurrencyValue(formValidationService.max.monthlyRentalPayment)
    ]);
  }

  // Builds off the maxValue validator to return valid only when value is less than the input number.
  // Refactor to use existing maxValue validator when possible.
  static ceilingValue(ceiling: number): ValidatorFn {
    return (c: FormControl): Object => {
      const outcome = UlaValidators.maxValue(ceiling - 1)(c);

      if (!!outcome) {
        return { ceilingValueError: `must be less than ${ceiling}` };
      }

      return null;
    };
  }

  // Builds off the minValue validator to return valid only when value is greater than the input number.
  // Refactor to use existing minValue validator when possible.
  static floorValue(floor: number): ValidatorFn {
    return (c: FormControl): Object => {
      const outcome = UlaValidators.minValue(floor + 1)(c);

      if (!!outcome) {
        return { floorValueError: `must be greater than ${floor}` };
      }

      return null;
    };
  }

  static mustBeValidDate(c: FormControl): Object {
    let dateFromForm: Date;
    let doesNotMatch: boolean;

    if (c.value instanceof Date) {
      doesNotMatch = false;
      dateFromForm = c.value;
    } else if (c.value) {
      // Try to parse this value into a date. If it fails, then it's not a valid date.
      try {
        const dateArray = c.value.split('/');
        dateFromForm = new Date(c.value);
        const monthDoesNotMatch = (Number(dateArray[0]) - 1) !== dateFromForm.getMonth();
        const dateDoesNotMatch = Number(dateArray[1]) !== dateFromForm.getDate();
        const yearDoesNotMatch = Number(dateArray[2]) !== dateFromForm.getFullYear();
        doesNotMatch = monthDoesNotMatch || dateDoesNotMatch || yearDoesNotMatch;
      } catch (e) {
        doesNotMatch = true;
      }
    }

    // empty string or null should be valid for this validator.
    // if empty values should not be allowed, use the "required" validator
    if ((!!c.value && dateFromForm.toString() === 'Invalid Date') || doesNotMatch) {
      return { mustBeValidDate: 'must be a valid date.' };
    }

    return null;
  }

  static getYearBuiltValidators(formValidationService: FormValidationService, isRequired: boolean = true) {
    let thisYear = new Date().getFullYear();

    const validatorArray = [
      Validators.maxLength(4),
      Validators.pattern(formValidationService.regex.yearBuilt),
      UlaValidators.validateDateMustBeInThePast,
      UlaValidators.floorValue(thisYear - 401)
    ];

    if (isRequired) {
      validatorArray.push(Validators.required);
    }

    return Validators.compose(validatorArray);
  }

  static getPriceValidators(formValidationService: FormValidationService, isRequired: boolean = true) {
    const validatorArray = [
      UlaValidators.positiveCurrencyValidator(formValidationService),
      Validators.maxLength(formValidationService.maxlength.positiveCurrency.mortgage)];

      if (isRequired) {
        validatorArray.push(Validators.required);
      }

    return Validators.compose(validatorArray);
  }

  static getPurchasePriceValidator(downPaymentControl: FormControl, isRequired: boolean = true) {
    return (control: FormControl) => {
      let errorResult = null;

      const bothControlsHaveValues = (!!control.value && !!downPaymentControl.value);

      if (isRequired && !bothControlsHaveValues) {
        errorResult = {
          maxValueError: 'enter a value'
        };
      }

      if (bothControlsHaveValues) {
        let pp = Utils.currencyStringToNumber(control.value);
        let dp = Utils.currencyStringToNumber(downPaymentControl.value);
        if (!!dp && !!pp && dp > pp) {
          errorResult = {
            maxValueError: 'must exceed down payment.'
          };
        }
      }
      return errorResult;
    };
  }

  static getCityStateZipRequired(firstControl: FormControl, secondControl: FormControl) {
    // control names are intentionally vague as each control could be any one of three different controls from the form
    return (control: FormControl) => {
      let errorResult = null;
      if (!control.value && !firstControl.value && !secondControl.value) {
        errorResult = {
          requiredCityStateZip: 'Enter one from city/state/zip'
        };
      }
      return errorResult;
    };
  }

  static getDownPaymentValidator(purchasePriceControl: FormControl, priceControlDisplayName: string,
                                 isRequired: boolean = true) {
    return (control: FormControl) => {
      let errorResult = null;

      const bothControlsHaveValues = (!!control.value && !!purchasePriceControl.value);

      if (isRequired && !bothControlsHaveValues) {
        errorResult = {
          maxValueError: 'enter a value'
        };
      }

      if (bothControlsHaveValues) {
        let pp = Utils.currencyStringToNumber(purchasePriceControl.value);
        let dp = Utils.currencyStringToNumber(control.value);

        if (!!pp && !!dp && dp > pp) {
          errorResult = {
            maxValueError: `cannot exceed ${priceControlDisplayName}.`
          };
        }
      }
      return errorResult;
    };
  }

  static getCityStateOrZipValidators(formValidationService: FormValidationService) {
    let m = formValidationService.maxlength;
    return Validators.compose([
      Validators.required,
      Validators.maxLength(m.city.mortgage + m.state + m.zipCode)
    ]);
  }

  static getPreviousDSLHValidators_MortgageFlows(formValidationService: FormValidationService,
                                                 dateStartedLivingHereControl: AbstractControl) {

    let previousDateIsNotLess = (control: FormControl) => {
      if (!control.value) {
        return null;
      }

      let pdslh = new Date(control.value);
      let dslh = new Date(dateStartedLivingHereControl.value);
      if (pdslh.getTime() > dslh.getTime()) {
        return {genericError: 'The previous address date shouldn\'t exceed the address date before it.'};
      }
      return null;
    };

    return Validators.compose([
      UlaValidators.getAllDateValidators_MortgageFlows(formValidationService, false, false),
      previousDateIsNotLess
    ]);
  }

  static getMilitaryPayRankValidators(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      UlaValidators.noEmptyText(),
      Validators.maxLength(formValidationService.maxlength.militaryPayRank.mortgage)
    ]);
  }

  static getMilitaryPaySalaryValidators(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      UlaValidators.maxCurrencyValue(formValidationService.max.militaryPayAnnualSalary.mortgage)
    ]);
  }

  static getPensionEmployerNameValidators_MortgageFlows(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      UlaValidators.noEmptyText(),
      Validators.maxLength(formValidationService.maxlength.employerName.mortgage)
    ]);
  }

  static getPensionMonthlyIncomeValidators_MortgageFlows(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      UlaValidators.maxCurrencyValue(formValidationService.max.pensionMonthlyIncome.mortgage)
    ]);
  }

  static getSocialSecurityMonthlyIncomeValidators_MortgageFlows(formValidationService: FormValidationService) {
    return Validators.compose([
      Validators.required,
      UlaValidators.maxCurrencyValue(formValidationService.max.socialSecurityMonthlyIncome.mortgage)
    ]);
  }

  static getMonthlyExpensesMortgageValidators(validationService: FormValidationService) {
    return Validators.compose([Validators.required,
      UlaValidators.maxCurrencyValue(validationService.max.monthlyExpensesMortgage)]);
  }

  // used when a control should required,
  // but ONLY when a predicate function returns true
  static getConditionalRequiredValidators(predicateFn: PredicateFn): ValidatorFn {
    const validatorFn = (control: FormControl) => {

      let result = null;

      if (predicateFn()) {

        if (!control.value && control.value !== false) {
          result = {
            required: ''
          };
        }
      }

      return result;
    };

    return Validators.compose([validatorFn]);
  }

  // used when a control should required and have a max value,
  // but ONLY when a predicate function returns true
  static getConditionalMaxValueValidators(predicateFn: PredicateFn,
                                          maxValue: number): ValidatorFn {
    const validatorFn = (control: FormControl) => {

      let result = null;

      if (predicateFn()) {

        const currencyValue = UlaValidators.getCurrencyValueFromControl(control);
        if (!control.value) {
          result = {
            required: ''
          };
        } else if (currencyValue > maxValue) {
          result = {
            maxCurrencyError: `must be ${maxValue} or less`
          };
        }
      }

      return result;
    };

    return Validators.compose([validatorFn]);
  }

  static conditionallyValidateDateMustBeInThePast(predicate: PredicateFn): ValidatorFn {
    const validatorFn: (c: FormControl) => ValidatorFn = (control: FormControl) => {

      let result = null;
      const value = control.value;

      if (value && predicate()) {
        const outcome = Utils.calculateMillisecondDiffFromToday(value);
        return outcome > 0 ? {genericError: 'Cannot select a future date.'} : null;
      }

      return result;
    };

    return Validators.compose([validatorFn]);
  }

  static getConditionalPatternValidator(predicateFn: PredicateFn,
                                        pattern: string | RegExp): ValidatorFn {
    const validatorFn = (control: FormControl) => {

      let result = null;

      if (predicateFn()) {
        result = Validators.pattern(pattern);
      }

      return result;
    };

    return Validators.compose([validatorFn]);
  }

  static getConditionalMaxLengthValidator(predicateFn: PredicateFn,
                                          maxLength: number): ValidatorFn {
    const validatorFn = () => {

      let result = null;

      if (predicateFn()) {
        result = Validators.maxLength(maxLength);
      }

      return result;
    };

    return Validators.compose([validatorFn]);
  }

  // uses a combination of flags to generate the appropriate validators.
  // this is for the common cases of "this is required and has a max value."
  // for instances of co-validation, those may require a specific method
  static getGenericValidators(controlName: string,
                              validationService: FormValidationService,
                              flags: UlaValidatorFlags = {required: true}): ValidatorFn {
    const {
      regex,
      maxlength,
      max
    } = validationService;

    // TODO: some of the validationService properties are a simple value,
    // some are an object with either Mortgage or PL. How to solve that problem?
    const controlRegex = regex[controlName];
    const controlMaxLength = maxlength[controlName];
    const controlMaxValue = max[controlName];

    let validatorArr = [];

    if (flags.required) {
      validatorArr.push(Validators.required);
    }

    if (!!controlRegex) {
      validatorArr.push(Validators.pattern(controlRegex));
    }

    if (!!controlMaxLength) {
      validatorArr.push(Validators.maxLength(controlMaxLength));
    }

    if (!!controlMaxValue) {
      if (flags.valueType === 'number') {
        validatorArr.push(UlaValidators.maxValue(controlMaxValue));
      } else if (flags.valueType === 'currency') {
        validatorArr.push(UlaValidators.maxCurrencyValue(controlMaxValue));
      }
    }

    return Validators.compose(validatorArr);
  }
}
